import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { http } from "./api";
import EvidenceTab from "./EvidenceTab";
import OutputsTab from "./OutputsTab";
import MessagesTab from "./MessagesTab";

const TABS = ["overview", "evidence", "outputs", "messages"];

export default function PortalEngagement() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "overview";
  const [eng, setEng] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get(`/portal/client/engagements/${id}`)
      .then(({ data }) => setEng(data))
      .catch(() => toast.error("Could not load this engagement."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone2">Loading engagement…</p>;
  if (!eng) return <p className="text-stone2">Engagement not found. <Link to="/portal" className="text-ember">Back to dashboard</Link></p>;

  return (
    <div data-testid="portal-engagement">
      <Link to="/portal" className="font-mono text-[11px] uppercase tracking-[0.15em] text-stone2 hover:text-ember transition-colors duration-300">← Dashboard</Link>
      <div className="flex flex-wrap items-baseline justify-between gap-3 mt-4 mb-8">
        <h1 className="font-serif text-3xl md:text-4xl">{eng.title}</h1>
        <span className="font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border border-ember/50 text-ember" data-testid="engagement-detail-status">{eng.status}</span>
      </div>

      <div className="flex items-center gap-6 border-b border-line mb-8 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setParams({ tab: t })} data-testid={`portal-tab-${t}`}
            className={`font-mono text-xs uppercase tracking-[0.2em] pb-3 -mb-px border-b-2 transition-colors duration-300 ${tab === t ? "border-ember text-ember" : "border-transparent text-stone2 hover:text-bone"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid md:grid-cols-2 gap-px bg-line/40 border border-line/40" data-testid="engagement-overview">
          <div className="bg-ink p-6 md:p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre mb-2">Bigibila contact</p>
            <p className="text-sm text-bone mb-6">{eng.bigibila_contact}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre mb-2">Engagement status</p>
            <p className="text-sm text-bone">{eng.status}</p>
          </div>
          <div className="bg-ink p-6 md:p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre mb-2">Upcoming client actions</p>
            {eng.client_actions?.length ? eng.client_actions.map((a, i) => <p key={i} className="text-sm text-stone2 mb-1">— {a}</p>) : <p className="text-sm text-stone2 mb-1">None at this time.</p>}
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre mt-6 mb-2">Key dates</p>
            {eng.key_dates?.length ? eng.key_dates.map((d, i) => <p key={i} className="text-sm text-stone2 mb-1">— {d.label}: <span className="text-bone">{d.date}</span></p>) : <p className="text-sm text-stone2">None recorded.</p>}
          </div>
        </div>
      )}
      {tab === "evidence" && <EvidenceTab engagementId={id} />}
      {tab === "outputs" && <OutputsTab engagementId={id} />}
      {tab === "messages" && <MessagesTab engagementId={id} />}
    </div>
  );
}
